import { useCallback, useEffect, useRef, useState } from 'react';
import { notification } from 'antd';
import { useSelector } from 'react-redux';
import axios from 'axios';
import moment from 'moment';

import { IInvestimentoACadastrar } from './interfaces';
import { stateGlobal } from '../../../interfaces/Global';

import { useGetColumnSearchProps } from '../../../hooks/FilterTable/stringCLearFiltros';
import { useGetColumnSearchPropsObj } from '../../../hooks/FilterTable/objString';
import { useGetColumnSearchPropsData } from '../../../hooks/FilterTable/date';
import { convertMoedaBRDoJSComExtamente2CasasDecimais } from '../../../services/Conversores';
import InvestimentosACadastrarService from '../../../services/http/services/SaldoDeContas/InvestimentosACadastrarService';

export const useInvestimentosACadastrar = () => {
    const propsState = useSelector((state: stateGlobal) => state.global);

    const [selectedRowKeys, setSelectedRowKeys] = useState<Array<number>>([]);
    const [investimentosACadastrar, setInvestimentosACadastrar] = useState<IInvestimentoACadastrar[]>([]);
    const [hasSelectedCadastrar, setHasSelectedCadastrar] = useState(false);
    const [loadingTable, setLoadingTable] = useState(false);
    const [investimentoSelecionado, setInvestimentoSelecionado] = useState<IInvestimentoACadastrar | null>(null);

    const refContagemDoFiltro = useRef<Array<IInvestimentoACadastrar>>([]);

    const buscaDadosInvestimentosACadastrar = useCallback((): void => {
        const signal = axios.CancelToken.source();
        setLoadingTable(true);

        const fetchData = async () => {
            try {
                const data = await InvestimentosACadastrarService.getInvestimentosACadastrar(signal.token);
                setInvestimentosACadastrar(data);
            } catch (error) {
                let msgErro: any = error as Error;
                if (msgErro?.message !== 'Requisicao cancelada!') {
                    notification.error({
                        message: 'Erro',
                        description: 'Não foi possível buscar os dados de investimentos a cadastrar!',
                    });
                }
            } finally {
                setLoadingTable(false);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        buscaDadosInvestimentosACadastrar();
    }, [buscaDadosInvestimentosACadastrar]);

    const clickBotaoCadastrarInvestimento = useCallback((): void => {
        if (selectedRowKeys.length === 1) {
            const investimento = investimentosACadastrar.find((e) => e.id === selectedRowKeys[0]);
            if (investimento) {
                setInvestimentoSelecionado(investimento);
            }
        }
    }, [selectedRowKeys, investimentosACadastrar]);

    const handleResetRowSelection = useCallback(() => {
        setSelectedRowKeys([]);
        setHasSelectedCadastrar(false);
    }, []);

    const onclickTable = useCallback((record: IInvestimentoACadastrar): void => {
        if (selectedRowKeys.includes(record.id)) {
            setSelectedRowKeys([]);
            setHasSelectedCadastrar(false);
        } else {
            setSelectedRowKeys([record.id]);
            setHasSelectedCadastrar(true);
        }
    }, [selectedRowKeys]);

    const handleInvestimentoCreated = useCallback((investimentoACadastrarId: number) => {
        setInvestimentosACadastrar((prevState) => prevState.filter((item) => item.id !== investimentoACadastrarId));
        handleResetRowSelection();
        window.dispatchEvent(new Event('atualizarContagemBalon'));
    }, [handleResetRowSelection]);

    const handleCloseModal = useCallback(() => {
        setInvestimentoSelecionado(null);
    }, []);

    const onSelectChange = useCallback((keys: number[]): void => {
        if (keys.length > 1) {
            setSelectedRowKeys([keys[1]]);
            setHasSelectedCadastrar(true);
        } else if (keys.length === 0) {
            setSelectedRowKeys([]);
            setHasSelectedCadastrar(false);
        } else {
            setSelectedRowKeys([keys[0]]);
            setHasSelectedCadastrar(true);
        }
    }, []);

    const rowSelection = {
        selectedRowKeys,
        onChange: (keys: any[]) => onSelectChange(keys as number[]),
        columnWidth: '1%',
        type: 'radio' as const,
    };

    const refFuncaoLimparFiltrosStringNome = useRef<any>(null);
    const refFuncaoLimparFiltrosStringValor = useRef<any>(null);
    const columnSearchPropsNome = useGetColumnSearchProps('nome', refFuncaoLimparFiltrosStringNome);
    const columnSearchPropsValor = useGetColumnSearchProps('valor', refFuncaoLimparFiltrosStringValor);

    const columns = [
        {
            title: 'Nome',
            dataIndex: 'nome',
            ...columnSearchPropsNome,
            width: '18%',
            sorter: (a: IInvestimentoACadastrar, b: IInvestimentoACadastrar) => a.nome.localeCompare(b.nome),
            render: (nome: string) => nome,
        },
        {
            title: 'Valor',
            dataIndex: 'valor',
            ...columnSearchPropsValor,
            width: '12%',
            sorter: (a: IInvestimentoACadastrar, b: IInvestimentoACadastrar) => a.valor - b.valor,
            render: (valor: number) => `R$ ${convertMoedaBRDoJSComExtamente2CasasDecimais(valor, true)}`,
        },
        {
            title: 'Mês / ano',
            dataIndex: 'mes',
            width: '10%',
            sorter: (a: IInvestimentoACadastrar, b: IInvestimentoACadastrar) => (a.ano * 12 + a.mes) - (b.ano * 12 + b.mes),
            render: (_: any, record: IInvestimentoACadastrar) => `${String(record.mes).padStart(2, '0')}/${record.ano}`,
        },
        {
            title: 'Banco',
            dataIndex: 'banco',
            ...useGetColumnSearchPropsObj('banco', 'nome'),
            width: '14%',
            sorter: (a: IInvestimentoACadastrar, b: IInvestimentoACadastrar) => (a.banco?.nome || '').localeCompare(b.banco?.nome || ''),
            render: (banco: any) => banco?.nome || '-',
        },
        {
            title: 'Órgão',
            dataIndex: 'orgaoVinculado',
            ...useGetColumnSearchPropsObj('orgaoVinculado', 'orgaoNome'),
            width: '16%',
            sorter: (a: IInvestimentoACadastrar, b: IInvestimentoACadastrar) => (a.orgaoVinculado?.orgao?.nome || '').localeCompare(b.orgaoVinculado?.orgao?.nome || ''),
            render: (orgaoVinculado: any) => orgaoVinculado?.orgao?.nome || '-',
        },
        {
            title: 'Órgão vinculado',
            dataIndex: 'orgaoVinculado',
            ...useGetColumnSearchPropsObj('orgaoVinculado', 'nome'),
            width: '16%',
            sorter: (a: IInvestimentoACadastrar, b: IInvestimentoACadastrar) => (a.orgaoVinculado?.nome || '').localeCompare(b.orgaoVinculado?.nome || ''),
            render: (orgaoVinculado: any) => orgaoVinculado?.nome || '-',
        },
        {
            title: 'Atualizado em',
            dataIndex: 'atualizadoEm',
            ...useGetColumnSearchPropsData('atualizadoEm'),
            width: '14%',
            sorter: (a: IInvestimentoACadastrar, b: IInvestimentoACadastrar) =>
                (a.atualizadoEm ? moment(a.atualizadoEm).unix() : 0) -
                (b.atualizadoEm ? moment(b.atualizadoEm).unix() : 0),
            render: (atualizadoEm: string | null) => (
                <span style={{ whiteSpace: 'nowrap' }}>
                    {atualizadoEm ? moment(atualizadoEm).format('DD/MM/YYYY HH:mm') : '-'}
                </span>
            ),
        },
    ];

    return {
        clickBotaoCadastrarInvestimento,
        hasSelectedCadastrar,
        loadingTable,
        onclickTable,
        rowSelection,
        investimentosACadastrar,
        columns,
        refContagemDoFiltro,
        propsState,
        handleResetRowSelection,
        investimentoSelecionado,
        handleCloseModal,
        handleInvestimentoCreated,
    };
};
